import {
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type JSX,
  type ComponentType,
} from 'react';
import Box from '@mui/material/Box';
import { type TooltipProps } from '@mui/material/Tooltip';
import { useIsTouchDevice } from '../../../hooks/useIsTouchDevice';
import { useMenuCellStyles, useMenuCellTitleStyles } from './menuCell.styles';
import { BodyS, Caption } from '../../../typography';
import { Tooltip } from '../tooltip';
import type { IconProps } from '@doxyme/icons';
import { useTheme } from '../../../theme';

export type MenuCellProps = {
  icon?: ComponentType<IconProps> | ReactNode;
  label: string;
  mark?: string | ReactElement;
  isMarkVisibleOnlyOnHover?: boolean;
  onClick?: () => void | Promise<void>;
  /**
   * Controls whether the MenuCell is disabled
   */
  disabled?: boolean;
  /**
   * Controls whether to show a tooltip when the MenuCell is disabled
   */
  disabledTooltipText?: string | ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
  style?: CSSProperties;
  destructive?: boolean;
  className?: string;
  triggerElementId?: string;
  title?: never;
};

type MenuCellTitleProps = {
  title: string;
  isMarkVisibleOnlyOnHover?: never;
  disabled?: never;
  disabledTooltipText?: never;
  destructive?: never;
  tooltipPlacement?: never;
  triggerElementId?: never;
  icon?: never;
  label?: never;
  mark?: never;
};

export const MenuCell = ({
  icon,
  label,
  mark,
  isMarkVisibleOnlyOnHover = false,
  disabled = false,
  disabledTooltipText,
  destructive = false,
  tooltipPlacement = 'left',
  triggerElementId,
  title,
  ...rest
}: MenuCellProps | MenuCellTitleProps): JSX.Element => {
  const theme = useTheme();
  const titleStyles = useMenuCellTitleStyles();
  const isTouchDevice = useIsTouchDevice();

  const menuCellStyles = useMenuCellStyles({
    isMarkVisibleOnlyOnHover: isTouchDevice ? false : isMarkVisibleOnlyOnHover,
  });

  if (Boolean(title)) {
    return (
      <Box css={titleStyles.titleContainer}>
        <Caption fontWeight='medium' color={theme.text.generic.tertiary}>
          {title}
        </Caption>
      </Box>
    );
  }

  return (
    <Tooltip
      title={disabledTooltipText}
      placement={tooltipPlacement}
      isDisabled={!(disabled && disabledTooltipText !== undefined)}
    >
      <span css={menuCellStyles.wrapper}>
        <button
          type='button'
          disabled={disabled}
          {...rest}
          role='menuitem'
          data-trigger-element-id={triggerElementId}
          css={[menuCellStyles.base, destructive && menuCellStyles.destructive]}
        >
          {icon && typeof icon === 'function' ? (
            <Box
              component={icon}
              size='small'
              type='stroke'
              css={{ flexShrink: 0 }}
            />
          ) : (
            icon
          )}
          <BodyS tag='span' fontWeight='medium' css={menuCellStyles.label}>
            {label}
          </BodyS>
          {Boolean(mark) && <span>{mark}</span>}
        </button>
      </span>
    </Tooltip>
  );
};
